<script setup>
import CustomerSearch from "@/components/CustomerSearch.vue";
import { ref } from "vue";

const emits = defineEmits(["changeCustomer"]);
const props = defineProps({
  reference: { type: Object, required: true },
  editable: { type: Boolean, default: false },
  embedded: { type: Boolean, default: false },
});
const showCustomerPicker = ref(false);
const invoicePaymentStatusLabel = (invoice) =>
  invoice?.payment_status_label ||
  (invoice?.paid_at ? "Paid" : invoice?.sent_at ? "Unpaid" : "Unsent");
const invoicePaymentStatusClass = (invoice) => {
  switch (invoicePaymentStatusLabel(invoice)) {
    case "Paid":
      return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300";
    case "Unpaid":
      return "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300";
    default:
      return "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300";
  }
};
const changeCustomer = (customer) => {
  emits("changeCustomer", customer);
  showCustomerPicker.value = false;
};
</script>

<template>
  <form-box header="Job overview" :box="!embedded" :show-header="!embedded">
    <dl class="reference-summary-grid">
      <div class="reference-summary-item">
        <dt>Job status</dt>
        <dd>
          <span
            class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
            :class="
              reference.accepted_at
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
            "
          >
            <span
              class="h-1.5 w-1.5 rounded-full bg-current"
              aria-hidden="true"
            ></span>
            {{ reference.accepted_at ? "Accepted" : "Pending" }}
          </span>
        </dd>
      </div>
      <div class="reference-summary-item">
        <dt>Reference</dt>
        <dd><ReferenceNumber :reference="reference" /></dd>
      </div>
      <div class="reference-summary-item reference-summary-customer">
        <dt>Customer</dt>
        <dd class="space-y-2">
          <div class="flex flex-wrap items-center gap-2">
            <router-link
              v-if="reference.customer?.id"
              :to="`/customer/${reference.customer.id}`"
              class="reference-summary-link break-words"
              >{{ reference.customer.company }}</router-link
            >
            <span v-else class="text-sm text-slate-500 dark:text-slate-400"
              >No customer assigned</span
            >
            <button
              v-if="editable && reference.customer?.id"
              type="button"
              class="reference-summary-change"
              :aria-expanded="showCustomerPicker"
              @click="showCustomerPicker = !showCustomerPicker"
            >
              {{ showCustomerPicker ? "Done" : "Change" }}
            </button>
          </div>
          <CustomerSearch
            v-if="editable && (showCustomerPicker || !reference.customer?.id)"
            :customer="reference.customer"
            :resetAfterSelection="false"
            @selected="changeCustomer"
          />
        </dd>
      </div>
      <div class="reference-summary-item">
        <dt>Invoice</dt>
        <dd>
          <router-link
            v-if="reference.invoice?.id"
            :to="`/invoice/${reference.invoice.id}`"
            class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
            :class="invoicePaymentStatusClass(reference.invoice)"
          >
            {{ invoicePaymentStatusLabel(reference.invoice) }}
          </router-link>
          <span v-else class="text-sm text-slate-500 dark:text-slate-400"
            >Not created</span
          >
        </dd>
      </div>
    </dl>
  </form-box>
</template>

<style scoped>
.reference-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 9rem), 1fr));
  gap: 1rem 1.5rem;
}
.reference-summary-item {
  min-width: 0;
}
.reference-summary-item dt {
  @apply mb-1.5 text-xs font-medium text-slate-500 dark:text-slate-400;
}
.reference-summary-item dd {
  @apply text-sm font-medium text-slate-900 dark:text-slate-100;
}
.reference-summary-link {
  @apply rounded-sm underline decoration-slate-300 underline-offset-4 hover:text-sky-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:decoration-slate-600 dark:hover:text-sky-300;
}
.reference-summary-change {
  @apply inline-flex min-h-[2.25rem] items-center rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700;
}
</style>
